import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import { Layout } from "plotly.js";
import { useMaterialColor, useMaterialDisplayName } from "hooks/useMaterials";
import { HeatSeries } from "src/store/api/generatedApi";
import { ChunkData } from "components/scrapTracking/helpers";

interface Props {
  heatsData: { series: HeatSeries[] } | undefined;
  start: Date;
  end: Date;
  chunks: ChunkData<unknown>[];
}

export const CumulativeMaterialUsagePlot = ({
  heatsData,
  start,
  end,
  chunks,
}: Props) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { getColor } = useMaterialColor();
  const materialDisplayName = useMaterialDisplayName();

  const loadingShapes = useMemo(() => {
    return chunks
      .filter((chunk) => chunk.value.status !== "success")
      .map((chunk) => ({
        type: "rect" as const,
        xref: "x" as const,
        yref: "paper" as const,
        x0: new Date(chunk.start),
        x1: new Date(chunk.end),
        y0: 0,
        y1: 1,
        fillcolor: "grey",
        opacity: 0.15,
        layer: "below" as const,
        line: { width: 0 },
      }));
  }, [chunks]);

  // Extract unique material names from the heats data
  const materials = useMemo(() => {
    if (!heatsData?.series) return [];

    const materialSet = new Set<string>();
    heatsData.series.forEach((heat) => {
      heat.drops.forEach((drop) => {
        materialSet.add(drop.material_name);
      });
    });

    // Sort alphabetically for consistent ordering
    return Array.from(materialSet).sort();
  }, [heatsData]);

  const plotData = useMemo(() => {
    if (!heatsData?.series || materials.length === 0) {
      return [];
    }

    // Sort heats by start time
    const sortedHeats = [...heatsData.series].sort(
      (a, b) =>
        new Date(a.start_timestamp).getTime() -
        new Date(b.start_timestamp).getTime()
    );

    // Initialize cumulative data with Date objects for timezone shifting
    const cumulativeData: Record<
      string,
      { x: Date[]; y: number[]; runningTotal: number }
    > = {};
    materials.forEach((material) => {
      cumulativeData[material] = { x: [], y: [], runningTotal: 0 };
    });

    sortedHeats.forEach((heat) => {
      if (heat.drops.length === 0) return;

      const timestamp = new Date(heat.start_timestamp);

      // Count lifts per material for this heat
      const liftCounts: Record<string, number> = {};
      materials.forEach((material) => {
        liftCounts[material] = 0;
      });

      heat.drops.forEach((drop) => {
        const materialName = drop.material_name;
        if (liftCounts[materialName] !== undefined) {
          liftCounts[materialName]++;
        }
      });

      // Update cumulative totals and add data points
      materials.forEach((material) => {
        const count = liftCounts[material] ?? 0;
        const entry = cumulativeData[material];
        if (count > 0 && entry) {
          entry.runningTotal += count;
          entry.x.push(timestamp);
          entry.y.push(entry.runningTotal);
        }
      });
    });

    // Create plot traces for each material with step lines for cumulative view
    return materials.map((material) => ({
      x: cumulativeData[material]?.x ?? [],
      y: cumulativeData[material]?.y ?? [],
      type: "scatter" as const,
      mode: "lines" as const,
      name: materialDisplayName(material),
      line: {
        color: getColor(material),
        width: 2,
        shape: "hv" as const,
      },
      hovertemplate: `${materialDisplayName(material)}: %{y} total grabs<extra></extra>`,
    }));
  }, [heatsData, materials, getColor, materialDisplayName]);

  const layout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        range: [start, end],
        type: "date",
        showgrid: true,
        gridcolor: theme.palette.grey[100],
      },
      yaxis: {
        showgrid: true,
        gridcolor: theme.palette.grey[100],
        title: { text: t("totalGrabs") },
        rangemode: "tozero",
      },
      margin: { t: 20, r: 20, l: 60, b: 40 },
      height: 300,
      showlegend: true,
      legend: { orientation: "h", y: 1.15 },
      hovermode: "x unified",
      dragmode: "pan",
      shapes: loadingShapes,
    }),
    [start, end, t, loadingShapes, theme.palette.grey]
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("cumulativeMaterialUsage")}
        </Typography>
        <Box sx={{ height: 300 }}>
          <ScrapTrackingViewportPlot
            data={plotData}
            layout={layout}
            style={{ width: "100%", height: "100%" }}
            config={{
              displayModeBar: false,
              responsive: true,
              scrollZoom: false,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
